import React, { createContext, useContext, useReducer } from 'react';
import PropTypes from 'prop-types';

import { getDisplayName } from './components/utils.js';

import {
  addLink,
  filterKeywordsByFunc,
  flattenKeywords,
  sortKeywords
} from './components/matrix/handeKeywords';

export const AppContext = createContext();

export function useAppState() {
  return useContext(AppContext);
}

const toggleClusterCollapsed = (state, action) => {
  if (!action.cluster) {
    return state;
  }

  const newClusterData = state.clusterData.map((c) =>
    c.id === action.cluster.id ? { ...c, expanded: !c.expanded } : c
  );

  let newOrderedKeywords;
  if (action.cluster.expanded) {
    // We are collapsing a cluster, so keep keywords that are  in a remaining cluster, or is used in filter
    newOrderedKeywords = filterKeywordsByFunc(
      state.matrixKeywordOrdering,
      (keyword) => {
        if (state.filterKeywords.includes(keyword)) {
          return true;
        }

        for (let i = 0; i < newClusterData.length; i++) {
          const cluster = newClusterData[i];
          if (cluster.expanded && cluster.keywords.includes(keyword)) {
            return true;
          }
        }
      }
    );
  } else {
    // We are opening a single new cluster

    newOrderedKeywords = getNewOrderedKeywords(state, newClusterData);
  }

  const newState = {
    ...state,
    clusterData: newClusterData,
    matrixKeywordOrdering: newOrderedKeywords,
    matrixVisibleKeywords: flattenKeywords(newOrderedKeywords)
  };

  // Need to update clustersInTable
  return updateDocumentsInTable(newState);
};

const getNewOrderedKeywords = (state, newClusterData) => {
  const existingKeywords = flattenKeywords(state.matrixKeywordOrdering);

  let extraKeywords = [
    ...[].concat(
      ...newClusterData
        .filter((c) => c.expanded)
        .map((c) => c.keywords.map((k) => k))
    ),
    ...state.filterKeywords.map((k) => k) // ?
  ].filter((k) => !existingKeywords.includes(k));

  extraKeywords = extraKeywords.filter(
    (k, i) => extraKeywords.indexOf(k) === i
  );

  return [...state.matrixKeywordOrdering, ...extraKeywords.map((k) => [k])];
};

const updateKeywordData = (state) => {
  // from this, compute mapping from cluster -> words in text [not necessarily keywords]
  if (Object.keys(state.documentCluster).length === 0) {
    return state;
  }

  let keywords_in_cluster = {};
  let keyword_frequency_count = {};

  const filteredDocumentIDs = state.documentsInTable.map((d) => d.id);

  for (let keyword in state.tdMatrix) {
    keyword_frequency_count[keyword] = state.tdMatrix[
      keyword
    ].filter((doc_id) => filteredDocumentIDs.includes(doc_id)).length;

    for (let i in state.tdMatrix[keyword]) {
      const doc_id = state.tdMatrix[keyword][i];

      if (!filteredDocumentIDs.includes(doc_id)) {
        continue;
      }

      const cluster = state.documentCluster[doc_id];

      if (!keywords_in_cluster[cluster]) {
        keywords_in_cluster[cluster] = [];
      }
      if (!keywords_in_cluster[cluster][keyword]) {
        keywords_in_cluster[cluster][keyword] = 0;
      }
      keywords_in_cluster[cluster][keyword] += 1;
    }
  }

  return {
    ...state,
    keywordsInCluster: keywords_in_cluster,
    keywordFrequency: keyword_frequency_count
  };
};

const updateDocumentsInTable = (state) => {
  const filterDocument = (doc) => {
    const fields = [
      'title',
      'filename',
      'version',
      'edit_date',
      'org_name',
      'org_level'
    ];
    for (let i = 0; i < fields.length; i++) {
      const value = doc[fields[i]];
      if (
        value &&
        value.toLowerCase().includes(state.filterString.toLowerCase())
      ) {
        return true;
      }
    }
  };

  const docIncludesFilterKeywords = (doc) => {
    for (let i = 0; i < state.filterKeywords.length; i++) {
      if (!state.tdMatrix[state.filterKeywords[i]].includes(doc.id)) {
        return false;
      }
    }
    return true;
  };

  let documentsInTable;
  if (state.documents) {
    if (state.documentResults && state.searchString) {
      // if searchString is empty, the search results will be empty too, so don't filter using them
      // could directly filter documentResults isntead?
      const resultDocumentIds = state.documentResults
        .filter((doc) => filterDocument(doc))
        .map((doc) => doc.id);
      documentsInTable = state.documents
        .filter(
          (doc) => filterDocument(doc) && resultDocumentIds.includes(doc.id)
        )
        .filter(docIncludesFilterKeywords);
    } else {
      documentsInTable = state.documents
        .filter(filterDocument)
        .filter(docIncludesFilterKeywords);
    }
  }

  const sortFunc = (a, b) => {
    if (state.sortGroupsBy === 'numDocs') {
      return b.members.length - a.members.length;
    } else if (state.sortGroupsBy === 'groupName') {
      const labelA = a.labels || '';
      const labelB = b.labels || '';
      return labelA < labelB ? -1 : labelA > labelB ? 1 : 0;
    } else {
      return 0;
    }
  };

  let clusters = false;
  if (state.clusterData) {
    const documentIdsInTable = documentsInTable.map((d) => d.id);

    clusters = state.clusterData
      .map((c) => ({
        ...c,
        members: c.members.filter((d) =>
          documentIdsInTable.includes(d.document_id)
        )
      }))
      .sort(sortFunc)
      .filter((c) => c.members.length > 0);
  }

  return updateKeywordData({
    ...state,
    documentsInTable,
    clustersInTable: clusters
  });
};

const appStateReducer = (state, action) => {
  switch (action.type) {
    case 'SET_DOCUMENTS': {
      console.log('Setting documents...');

      const generalExcludedFields = [
        'id',
        'hexid',
        'filename',
        'filetype',
        'created_on',
        'title',
        'version',
        'edit_date',
        'org_name',
        'org_level',
        'misc',
        'approved',
        'Name'
      ];

      const customExclusions = process.env.REACT_APP_HIDDEN_METADATA_FIELDS;
      const specificExcludedFields = customExclusions
        ? customExclusions.split(', ')
        : [];
      const excludedFields = [
        ...generalExcludedFields,
        ...specificExcludedFields
      ];

      const metadataColumns = Object.keys(action.documents[0]).filter(
        (k) => !excludedFields.includes(k)
      );

      return updateDocumentsInTable({
        ...state,
        documents: action.documents,
        visibleMetadataColumns: metadataColumns,
        metadataColumnVisibility: metadataColumns.map((col) => ({
          name: col,
          visible: true
        }))
      });
    }

    case 'SET_SELECTED_DOCUMENT': {
      return {
        ...state,
        selectedDocument: action.selectedDocument
      };
    }

    case 'SET_PAGE_NUMBER': {
      return { ...state, pageNumber: action.pageNumber };
    }

    case 'SET_CLUSTER_DATA': {
      let clusterLabels = [];
      let documentCluster = [];
      let documentClusterObj = [];

      for (let i = 0; i < action.clusterData.length; i++) {
        const id = action.clusterData[i].id;
        const labels = action.clusterData[i].labels;
        clusterLabels[id] = labels;

        for (let j = 0; j < action.clusterData[i].members.length; j++) {
          const doc_id = action.clusterData[i].members[j].document_id;
          documentCluster[doc_id] = id;
          documentClusterObj[doc_id] = action.clusterData[i];
        }
      }

      const reshapedClusters = action.clusterData.map((c) => ({
        ...c,
        expanded: false,
        keywords: c.labels.split(', ')
      }));

      return {
        ...state,
        clusterData: reshapedClusters,
        clusterDataCopy: JSON.parse(JSON.stringify(reshapedClusters)),
        clusterLabels: clusterLabels,
        documentCluster: documentCluster,
        documentClusterObj: documentClusterObj,
        documents: state.documents.map((doc) => {
          // console.log(doc);
          const cluster = documentClusterObj[doc.id];
          return {
            ...doc,
            'Cluster Keywords': cluster ? cluster.labels : ''
          };
        }),
        metadataColumnVisibility: [
          ...state.metadataColumnVisibility,
          { name: 'Cluster Keywords', visible: false }
        ]
      };
    }

    case 'SET_SELECTED_CLUSTER': {
      return { ...state, selectedCluster: action.selectedCluster };
    }

    case 'SET_MOUSED_OVER_CLUSTER': {
      return { ...state, mousedOverCluster: action.mousedOverCluster };
    }

    case 'SET_MOUSED_OVER_DOCUMENT': {
      return { ...state, mousedOverDocument: action.mousedOverDocument };
    }

    case 'SET_DOCUMENT_RESULTS': {
      const updatedCluster = state.clusterData.map((c) => ({
        ...c,
        expanded: false
      }));

      let newState = {
        ...state,
        documentResults: action.documentResults,

        /* Reset state of matrix view */
        filterKeywords: [],
        matrixVisibleKeywords: [],
        matrixKeywordOrdering: [],
        clusterData: updatedCluster,
        clusterDataCopy: updatedCluster
      };

      newState = updateDocumentsInTable(newState);

      return toggleClusterCollapsed(newState, {
        cluster: newState.clustersInTable[0]
      });
    }

    case 'SET_FILTER_STRING': {
      return { ...state, filterString: action.filterString };
    }

    case 'SET_SELECTED_SNIPPET': {
      return { ...state, selectedSnippet: action.selectedSnippet };
    }

    case 'SET_SEARCH_STRING': {
      return { ...state, searchString: action.searchString };
    }

    case 'SET_FILE_LIST': {
      return { ...state, fileList: action.fileList };
    }
    case 'SET_UPLOAD_TITLE': {
      return { ...state, uploadTitle: action.uploadTitle };
    }

    case 'SET_SHOW_RELATED_SNIPPETS': {
      return {
        ...state,
        showRelatedSnippets: {
          ...state.showRelatedSnippets,
          [action.snippetId]: action.value
        }
      };
    }

    case 'SET_SHOW_RELATED_DOCS': {
      return {
        ...state,
        showRelatedDocs: {
          ...state.showRelatedDocs,
          [action.documentId]: action.value
        }
      };
    }

    case 'SET_SEARCH_IN_PROGRESS': {
      return { ...state, searchInProgress: action.searchInProgress };
    }

    case 'SET_IS_EXPANDED_DOCUMENT_STATE': {
      return {
        ...state,
        isExpandedDocumentState: action.isExpandedDocumentState
      };
    }

    case 'SET_IS_EXPANDED_STATE': {
      console.log('Setting expanded state to:', action.isExpandedState);
      return { ...state, isExpandedState: action.isExpandedState };
    }

    /*  */
    case 'SET_FILTER_KEYWORDS': {
      return { ...state, filterKeywords: action.filterKeywords };
    }

    case 'TOGGLE_FILTER_KEYWORD': {
      let newState;
      if (state.filterKeywords.includes(action.keyword)) {
        newState = {
          ...state,
          filterKeywords: state.filterKeywords.filter(
            (k) => k !== action.keyword
          )
        };
      } else {
        newState = {
          ...state,
          filterKeywords: [...state.filterKeywords, action.keyword]
        };
      }

      return updateDocumentsInTable(newState);
    }

    case 'CLEAR_KEYWORD_FILTERS': {
      return updateDocumentsInTable({ ...state, filterKeywords: [] });
    }

    case 'EXPAND_ALL_CLUSTERS': {
      // TODO: there is some duplication of code with DocumentTermMatrix that could be removed
      let resultDocumentIds = [];
      if (state.documentResults) {
        resultDocumentIds = state.documentResults.map((d) => +d.id);
      }

      const newClusterData = state.clusterData.map((c) => ({
        ...c,
        expanded:
          c.expanded ||
          c.members.filter(
            (doc) =>
              resultDocumentIds.length === 0 ||
              resultDocumentIds.includes(doc.document_id)
          ).length > 0
      }));

      const newOrderedKeywords = getNewOrderedKeywords(state, newClusterData);

      return updateDocumentsInTable({
        ...state,
        clusterData: newClusterData,
        matrixKeywordOrdering: newOrderedKeywords,
        matrixVisibleKeywords: flattenKeywords(newOrderedKeywords)
      });
    }

    case 'COLLAPSE_ALL_CLUSTERS': {
      const filteredKeywords = filterKeywordsByFunc(
        state.matrixKeywordOrdering,
        (keyword) => state.filterKeywords.includes(keyword)
      );

      return updateDocumentsInTable({
        ...state,
        clusterData: state.clusterData.map((c) => ({ ...c, expanded: false })),
        matrixKeywordOrdering: filteredKeywords,
        matrixVisibleKeywords: flattenKeywords(filteredKeywords)
      });
    }

    case 'TOGGLE_CLUSTER_COLLAPSED': {
      return toggleClusterCollapsed(state, action);
    }

    case 'ADD_KEYWORD_ORDERING_LINK': {
      const newOrder = addLink(
        state.matrixKeywordOrdering,
        action.firstKeyword,
        action.secondKeyword
      );

      return {
        ...state,
        matrixKeywordOrdering: newOrder,
        matrixVisibleKeywords: flattenKeywords(newOrder)
      };
    }

    case 'SET_PDF_MODAL_OPEN': {
      return { ...state, pdfModalOpen: action.pdfModalOpen };
    }

    case 'SET_ONBOARIDNG_MODAL_OPEN': {
      console.log(action);
      return { ...state, onboardingModalOpen: action.onboardingModalOpen };
    }

    case 'SET_APPLIED_BRUSH': {
      return { ...state, appliedBrush: action.appliedBrush };
    }

    case 'SET_KEYWORD_ORDER': {
      switch (action.order) {
        case 'ALPHABETICAL': {
          const orderedKeywords = sortKeywords(
            state.matrixKeywordOrdering,
            (a, b) => (a < b ? -1 : a > b ? 1 : 0)
          );
          return {
            ...state,
            matrixKeywordOrdering: orderedKeywords,
            matrixVisibleKeywords: flattenKeywords(orderedKeywords)
          };
        }

        case 'FREQUENCY': {
          const orderedKeywords = sortKeywords(
            state.matrixKeywordOrdering,
            (a, b) => state.keywordFrequency[a] - state.keywordFrequency[b]
          );
          return {
            ...state,
            matrixKeywordOrdering: orderedKeywords,
            matrixVisibleKeywords: flattenKeywords(orderedKeywords)
          };
        }

        case 'CLUSTER': {
          const firstCluster = {};
          for (let i = state.clusterData.length - 1; i >= 0; i--) {
            for (let keyword of state.clusterData[i].keywords) {
              firstCluster[keyword] = i;
            }
          }

          const orderedKeywords = sortKeywords(
            state.matrixKeywordOrdering,
            (a, b) => firstCluster[a] - firstCluster[b]
          );
          return {
            ...state,
            matrixKeywordOrdering: orderedKeywords,
            matrixVisibleKeywords: flattenKeywords(orderedKeywords)
          };
        }

        default: {
          return state;
        }
      }
    }

    case 'SET_TD_MATRIX': {
      return updateDocumentsInTable({ ...state, tdMatrix: action.tdMatrix });
    }

    /* Metadata columns */
    case 'TOGGLE_METADATA_COLUMN': {
      const newMetadataColumnVisibility = state.metadataColumnVisibility.map(
        (d) =>
          d.name === action.columnName ? { ...d, visible: !d.visible } : d
      );

      const columnNames = newMetadataColumnVisibility
        .filter((col) => col.visible)
        .map((col) => col.name);

      return {
        ...state,
        metadataColumnVisibility: newMetadataColumnVisibility,
        visibleMetadataColumns: columnNames
      };
    }

    case 'REORDER_METADATA_COLUMNS': {
      const first = state.metadataColumnVisibility.filter(
        (k) => k.name === action.firstKeyword
      )[0];

      const second = state.metadataColumnVisibility.filter(
        (k) => k.name === action.secondKeyword
      )[0];

      const others = state.metadataColumnVisibility.filter(
        (k) => k.name !== action.secondKeyword
      );

      const index = others.indexOf(first);

      let newMetadataColumnVisibility = [
        ...others.slice(0, index + 1), // includes first
        second,
        ...others.slice(index + 1) // things after first
      ];

      const columnNames = newMetadataColumnVisibility
        .filter((col) => col.visible)
        .map((col) => col.name);

      return {
        ...state,
        metadataColumnVisibility: newMetadataColumnVisibility,
        visibleMetadataColumns: columnNames
      };
    }

    case 'GROUP_BY': {
      let res;

      if (action.columnName === 'None') {
        console.log('Regrouping by none');
        const groupData = [
          {
            expanded: true,
            id: 1,
            is_outlier: false,
            keywords: [],
            labels: 'All documnets',
            members: state.clusterData.map((c) => c.members).flat()
          }
        ];

        res = { ...state, groupBy: action.columnName, clusterData: groupData };
      } else if (action.columnName === 'Cluster') {
        res = {
          ...state,
          groupBy: action.columnName,
          clusterData: JSON.parse(JSON.stringify(state.clusterDataCopy))
        };
      } else {
        // Not one of the special cases, so we are grouping by a metadata column

        const allDocs = state.clusterData.map((c) => c.members).flat();

        let groups = {};

        for (let doc of allDocs) {
          const docObj = state.documents.filter(
            (d) => d.id === doc.document_id
          )[0];

          const value = docObj[action.columnName];

          if (!Object.keys(groups).includes(value)) {
            groups[value] = {
              expanded: true,
              id: Object.keys(groups).length + 1,
              is_outlier: false,
              keywords: [],
              labels: value,
              members: []
            };
          }

          groups[value].members.push({
            document_id: doc.document_id,
            id: groups[value].members.length + 1,
            x: doc.x,
            y: doc.y
          });
        }

        res = {
          ...state,
          groupBy: action.columnName,
          clusterData: Object.values(groups)
        };
      }

      let newMetadataColumnVisibility;
      if (action.columnName === 'Cluster') {
        console.log('Changing grouping TO cluster');

        newMetadataColumnVisibility = state.metadataColumnVisibility.map((d) =>
          d.name === 'Cluster Keywords' ? { ...d, visible: false } : d
        );
      } else if (state.groupBy === 'Cluster') {
        console.log('Changing grouping FROM cluster');
        newMetadataColumnVisibility = state.metadataColumnVisibility.map((d) =>
          d.name === 'Cluster Keywords' ? { ...d, visible: true } : d
        );
      } else {
        newMetadataColumnVisibility = state.metadataColumnVisibility;
      }

      const columnNames = newMetadataColumnVisibility
        .filter((col) => col.visible)
        .map((col) => col.name);

      res = {
        ...res,
        metadataColumnVisibility: newMetadataColumnVisibility,
        visibleMetadataColumns: columnNames
      };

      return updateDocumentsInTable(res); // need to set clustersInTable from clusterData

      // TODO: can move .x/.y ouy of this object - used only by scatterplot, which uses  clusterDataCopy
    }

    case 'SORT_BY': {
      // sort docs by

      let cmpFunc;
      if (action.columnName === 'Cluster') {
        cmpFunc = (a, b) => {
          return (
            state.documentClusterObj[a.document_id].id -
            state.documentClusterObj[b.document_id].id
          );
        };
      } else if (action.columnName === 'Title') {
        cmpFunc = (a, b) => {
          const aObj = state.documents.filter((d) => d.id === a.document_id)[0];
          const bObj = state.documents.filter((d) => d.id === b.document_id)[0];

          const aVal = getDisplayName(aObj);
          const bVal = getDisplayName(bObj);

          return aVal < bVal ? -1 : aVal > bVal ? +1 : 0;
        };
      } else {
        cmpFunc = (a, b) => {
          const aObj = state.documents.filter((d) => d.id === a.document_id)[0];
          const bObj = state.documents.filter((d) => d.id === b.document_id)[0];

          const aVal = aObj[action.columnName] || '';
          const bVal = bObj[action.columnName] || '';

          return aVal < bVal ? -1 : aVal > bVal ? +1 : 0;
        };
      }

      return updateDocumentsInTable({
        ...state,
        sortBy: action.columnName,
        clusterData: state.clusterData.map((c) => ({
          ...c,
          members: c.members.sort(cmpFunc)
        }))
      });
    }

    case 'SORT_GROUPS_BY': {
      const newState = {
        ...state,
        sortGroupsBy: action.sortGroupsBy
      };

      return updateDocumentsInTable(newState);
    }

    default: {
      console.log("Can't handle:", action);
      return state;
    }
  }
};

const initialState = {
  // "Complete" data
  documents: false, // details of every document
  clusterData: false, // full details of clusters: id, is_outlier, keywords, labels, members...
  documentCluster: [], // map from document_id  to cluster_id [or index?]
  clusterLabels: [], // list of labels for each cluster [redundant?]
  documentResults: false, // search results - list of documents objects matching search
  tdMatrix: null, // list of all documents containing each keyword

  // Filtered data
  documentsInTable: [], // filtered list of documents matching search and filter criteria
  clustersInTable: null,

  // for PDF
  pageNumber: 1,
  pdfModalOpen: false,

  // UI state for file upload
  fileList: [],
  uploadTitle: '',

  // for clusterList only
  showRelatedSnippets: {},
  showRelatedDocs: {},
  isExpandedDocumentState: false,

  // current UI state
  selectedDocument: false,
  selectedCluster: false,
  selectedSnippet: false,

  searchString: '',
  searchInProgress: false,

  isExpandedState: false,

  filterString: '',

  mousedOverCluster: null,
  mousedOverDocument: null,

  appliedBrush: null,

  onboardingModalOpen: !localStorage.getItem('helpHasBeenViewed'),

  // which columns to display in matrix
  filterKeywords: [],
  matrixVisibleKeywords: [], // TODO: this could be replaced by flattening matrixKeywordOrdering at point-of-use
  matrixKeywordOrdering: [],

  metadataColumnVisibility: [],
  visibleMetadataColumns: [],

  groupBy: 'Cluster',
  sortBy: null,
  sortGroupsBy: 'numDocs',
  clusterDataCopy: [] // used by things other than documentTermMatrix
};

export function AppStateProvider({ children }) {
  let reducer = useReducer(appStateReducer, initialState);

  return <AppContext.Provider value={reducer}>{children}</AppContext.Provider>;
}

AppStateProvider.propTypes = {
  children: PropTypes.object
};
